import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  alpha,
  useTheme,
  useMediaQuery,
  Drawer,
} from '@mui/material';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import proxy from '../api/axios';
import { toast } from 'react-toastify';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ProblemPage from './ProblemPage';
import { Helmet } from 'react-helmet';
import { formatProblemTitle } from '../common/utils';

const ACCENT = '#137274';
const PINK = '#f50057';

const DIFF_COLOR = {
  easy: '#1a7f37',
  medium: '#d97706',
  hard: '#cf222e',
};

const pad = (n) => String(Math.max(0, n)).padStart(2, '0');

const formatTime = (seconds) => {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
};

const problemLetter = (index) => String.fromCharCode(65 + index);

const LiveContest = () => {
  const { contestId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const pageBg = isDark ? '#0f1419' : '#f4f8f9';
  const muted = isDark ? '#8b949e' : '#5f6b73';

  const [contest, setContest] = useState(null);
  const [problems, setProblems] = useState([]);
  const [selectedProblem, setSelectedProblem] = useState('All');
  const [endsAt, setEndsAt] = useState(null); // absolute end timestamp (ms)
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [successDialog, setSuccessDialog] = useState(false);
  const [confirmSubmitDialog, setConfirmSubmitDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [finalized, setFinalized] = useState(false); // submitted or time-up handled
  const [drawerOpen, setDrawerOpen] = useState(false);
  const autoSubmitLock = React.useRef(false);

  const redirectAfterClose = useCallback(
    (code, message) => {
      toast.info(message || 'Contest closed');
      if (code === 'ALREADY_SUBMITTED' || code === 'TIME_OVER') {
        navigate(`/contests/${contestId}/result`);
      } else {
        navigate('/contests');
      }
    },
    [contestId, navigate],
  );

  useEffect(() => {
    let cancelled = false;
    const fetchContestDetails = async () => {
      try {
        setIsLoading(true);
        const { data } = await proxy.get(`/contests/${contestId}`);
        if (cancelled) return;
        setContest(data);
        setProblems(data.problems || []);

        const endMs = data.endsAt
          ? new Date(data.endsAt).getTime()
          : Date.now() +
            (typeof data.remainingSeconds === 'number'
              ? data.remainingSeconds * 1000
              : (data.duration || 0) * 60 * 1000);

        const remaining = Math.max(0, Math.floor((endMs - Date.now()) / 1000));
        if (remaining <= 0) {
          redirectAfterClose('TIME_OVER', 'Your time is over — check results');
          return;
        }
        setEndsAt(endMs);
        setTimeRemaining(remaining);
      } catch (error) {
        console.error('Error fetching contest details:', error);
        const code = error.response?.data?.code;
        const message =
          error.response?.data?.message || 'Failed to load contest';
        if (!cancelled) redirectAfterClose(code, message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchContestDetails();
    return () => {
      cancelled = true;
    };
  }, [contestId, redirectAfterClose]);

  const finalizeContest = useCallback(
    async ({ reason }) => {
      if (autoSubmitLock.current || finalized) return false;
      autoSubmitLock.current = true;
      try {
        await proxy.put(`/contests/${contestId}/submit`);
        setFinalized(true);
        if (reason === 'timeout') {
          setIsDialogOpen(true);
        } else if (reason === 'manual') {
          setConfirmSubmitDialog(false);
          setSuccessDialog(true);
        }
        return true;
      } catch (error) {
        console.error('Error submitting contest:', error);
        autoSubmitLock.current = false;
        if (reason === 'timeout') {
          // Time is over regardless — lock the arena and show results path
          setFinalized(true);
          setIsDialogOpen(true);
          return true;
        }
        throw error;
      }
    },
    [contestId, finalized],
  );

  // Absolute clock — avoids setInterval drift
  useEffect(() => {
    if (!endsAt || finalized) return undefined;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
      setTimeRemaining(remaining);
      if (remaining <= 0) {
        finalizeContest({ reason: 'timeout' });
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endsAt, finalized, finalizeContest]);

  const urgency = useMemo(() => {
    if (timeRemaining <= 60) return 'critical';
    if (timeRemaining <= 5 * 60) return 'warn';
    return 'ok';
  }, [timeRemaining]);

  const timerColor =
    urgency === 'critical' ? PINK : urgency === 'warn' ? '#d97706' : ACCENT;

  const handleSelectProblem = (problem) => {
    setSelectedProblem(problem);
    setDrawerOpen(false);
  };

  const handleTimeoutDialogClose = () => {
    setIsDialogOpen(false);
    navigate(`/contests/${contestId}/result`);
  };

  const handleConfirmSubmit = async () => {
    try {
      setSubmitting(true);
      await finalizeContest({ reason: 'manual' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not submit contest');
    } finally {
      setSubmitting(false);
    }
  };

  const ProblemRail = useCallback(
    ({ inDrawer = false }) => (
      <Box
        data-tour="live-contest-problems"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          bgcolor: surface,
          borderRight: inDrawer ? 'none' : `1px solid ${border}`,
        }}
      >
        <Box sx={{ px: 2, py: 1.75, borderBottom: `1px solid ${border}` }}>
          <Typography
            sx={{
              fontSize: '0.68rem',
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: muted,
              mb: 0.35,
            }}
          >
            Problems
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem' }}>
            {problems.length} question{problems.length === 1 ? '' : 's'}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
          <Box
            component="button"
            type="button"
            onClick={() => handleSelectProblem('All')}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              width: '100%',
              cursor: 'pointer',
              px: 2,
              py: 1.1,
              borderLeft: `3px solid ${selectedProblem === 'All' ? ACCENT : 'transparent'}`,
              bgcolor:
                selectedProblem === 'All'
                  ? alpha(ACCENT, isDark ? 0.18 : 0.1)
                  : 'transparent',
              '&:hover': { bgcolor: alpha(ACCENT, isDark ? 0.12 : 0.06) },
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1.25,
                display: 'grid',
                placeItems: 'center',
                fontWeight: 800,
                fontSize: '0.72rem',
                bgcolor: alpha(ACCENT, isDark ? 0.25 : 0.12),
                color: ACCENT,
              }}
            >
              ≡
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>Overview</Typography>
          </Box>

          {problems.map((problem, index) => {
            const active = selectedProblem === index;
            const diff = (problem.difficulty || 'easy').toLowerCase();
            return (
              <Box
                key={problem._id || index}
                component="button"
                type="button"
                onClick={() => handleSelectProblem(index)}
                sx={{
                  all: 'unset',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.25,
                  width: '100%',
                  cursor: 'pointer',
                  px: 2,
                  py: 1.15,
                  borderLeft: `3px solid ${active ? ACCENT : 'transparent'}`,
                  bgcolor: active ? alpha(ACCENT, isDark ? 0.18 : 0.1) : 'transparent',
                  '&:hover': { bgcolor: alpha(ACCENT, isDark ? 0.12 : 0.06) },
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.25,
                    flexShrink: 0,
                    display: 'grid',
                    placeItems: 'center',
                    fontWeight: 800,
                    fontSize: '0.78rem',
                    bgcolor: active
                      ? ACCENT
                      : isDark
                        ? alpha('#fff', 0.06)
                        : alpha(ACCENT, 0.08),
                    color: active ? '#fff' : isDark ? '#c9d1d9' : '#0f2a2b',
                  }}
                >
                  {problemLetter(index)}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: active ? 800 : 600,
                      fontSize: '0.84rem',
                      lineHeight: 1.35,
                      color: active ? ACCENT : 'inherit',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatProblemTitle(problem.title) || `Problem ${index + 1}`}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.25,
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      textTransform: 'capitalize',
                      color: DIFF_COLOR[diff] || muted,
                    }}
                  >
                    {diff}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [problems, selectedProblem, surface, border, muted, isDark],
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          bgcolor: pageBg,
        }}
      >
        <CircularProgress sx={{ color: ACCENT }} />
        <Typography sx={{ color: muted, fontWeight: 600 }}>Loading contest arena…</Typography>
      </Box>
    );
  }

  if (!contest) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: { xs: 'auto', md: '100%' },
        minHeight: { xs: 'calc(100dvh - 56px)', md: 0 },
        bgcolor: pageBg,
        overflow: { md: 'hidden' },
      }}
    >
      <Helmet>
        <title>{contest.title ? `${contest.title} · Live` : 'Live Contest'} | CrafterHack</title>
        <meta name="description" content="Live coding contest arena on CrafterHack." />
      </Helmet>

      {/* Sticky arena header */}
      <Box
        data-tour="live-contest-header"
        sx={{
          flexShrink: 0,
          borderBottom: `1px solid ${border}`,
          bgcolor: surface,
          zIndex: 5,
        }}
      >
        <Box
          sx={{
            width: '100%',
            px: { xs: 1.5, sm: 2 },
            py: 1.1,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 1.5 },
          }}
        >
          {isMobile && (
            <IconButton
              size="small"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open problems"
              sx={{ border: `1px solid ${border}`, borderRadius: 1.5 }}
            >
              <ListAltOutlinedIcon fontSize="small" />
            </IconButton>
          )}

          <Tooltip title="Back to contest details">
            <IconButton
              size="small"
              component={Link}
              to={`/contests/details/${contestId}`}
              sx={{
                border: `1px solid ${border}`,
                borderRadius: 1.5,
                display: { xs: 'none', sm: 'inline-flex' },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.15 }}>
              <Chip
                icon={
                  <FiberManualRecordIcon
                    sx={{
                      fontSize: '10px !important',
                      color: '#fff !important',
                      animation: 'livePulse 1.4s ease-in-out infinite',
                      '@keyframes livePulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.35 },
                      },
                    }}
                  />
                }
                label="Live"
                size="small"
                sx={{
                  height: 22,
                  fontWeight: 800,
                  fontSize: '0.65rem',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  bgcolor: PINK,
                  color: '#fff',
                  '& .MuiChip-icon': { ml: 0.55 },
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: muted,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Contest arena
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '0.92rem', sm: '1.05rem' },
                letterSpacing: '-0.02em',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {contest.title}
            </Typography>
          </Box>

          {/* Timer */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: { xs: 1.1, sm: 1.5 },
              py: 0.75,
              borderRadius: 2,
              border: `1px solid ${alpha(timerColor, 0.35)}`,
              bgcolor: alpha(timerColor, isDark ? 0.16 : 0.08),
              minWidth: { xs: 108, sm: 132 },
            }}
          >
            <TimerOutlinedIcon sx={{ color: timerColor, fontSize: { xs: 18, sm: 20 } }} />
            <Box>
              <Typography
                sx={{
                  fontSize: '0.58rem',
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: timerColor,
                  lineHeight: 1,
                  mb: 0.25,
                }}
              >
                Time left
              </Typography>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '0.95rem', sm: '1.1rem' },
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1,
                  color: isDark ? '#e6edf3' : '#0f2a2b',
                }}
              >
                {formatTime(timeRemaining)}
              </Typography>
            </Box>
          </Box>

          <Tooltip title="Take a quick tour">
            <IconButton
              size="small"
              onClick={() =>
                window.dispatchEvent(new CustomEvent('ch-start-contests-tour'))
              }
              sx={{
                border: `1px solid ${border}`,
                borderRadius: 1.5,
              }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<SendRoundedIcon />}
            onClick={() => setConfirmSubmitDialog(true)}
            disabled={finalized}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 2,
              px: { xs: 1.5, sm: 2 },
              bgcolor: ACCENT,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' },
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          >
            Final submit
          </Button>
          <IconButton
            onClick={() => setConfirmSubmitDialog(true)}
            disabled={finalized}
            sx={{
              display: { xs: 'inline-flex', sm: 'none' },
              bgcolor: ACCENT,
              color: '#fff',
              borderRadius: 2,
              '&:hover': { bgcolor: '#0f5c5e' },
              '&.Mui-disabled': { bgcolor: alpha(ACCENT, 0.4), color: '#fff' },
            }}
            aria-label="Final submit"
          >
            <SendRoundedIcon fontSize="small" />
          </IconButton>
        </Box>

        {urgency !== 'ok' && (
          <LinearProgress
            variant="determinate"
            value={Math.min(100, (timeRemaining / (5 * 60)) * 100)}
            sx={{
              height: 3,
              bgcolor: alpha(timerColor, 0.15),
              '& .MuiLinearProgress-bar': { bgcolor: timerColor },
            }}
          />
        )}
      </Box>

      {/* Body — full-bleed arena */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '280px minmax(0, 1fr)' },
          gridTemplateRows: 'minmax(0, 1fr)',
        }}
      >
        {!isMobile && (
          <Box
            sx={{
              minHeight: 0,
              overflow: 'hidden',
              bgcolor: surface,
              borderRight: `1px solid ${border}`,
            }}
          >
            <ProblemRail />
          </Box>
        )}

        <Box
          data-tour="live-contest-workspace"
          sx={{
            minHeight: 0,
            overflow: { md: 'auto' },
            bgcolor: pageBg,
          }}
        >
          {selectedProblem === 'All' ? (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', letterSpacing: '-0.02em', mb: 0.5 }}>
                Problem set
              </Typography>
              <Typography sx={{ color: muted, fontSize: '0.9rem', mb: 2.5, maxWidth: 520 }}>
                Pick a problem from the list. Your timer keeps running across all questions — submit
                when you’re done.
              </Typography>

              <Stack spacing={1.25}>
                {problems.map((problem, index) => {
                  const diff = (problem.difficulty || 'easy').toLowerCase();
                  return (
                    <Box
                      key={problem._id || index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1.75,
                        borderRadius: 2,
                        border: `1px solid ${border}`,
                        bgcolor: isDark ? alpha('#fff', 0.02) : '#f8fbfb',
                        transition: 'border-color 0.15s, background 0.15s',
                        '&:hover': {
                          borderColor: alpha(ACCENT, 0.45),
                          bgcolor: alpha(ACCENT, isDark ? 0.1 : 0.04),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          flexShrink: 0,
                          display: 'grid',
                          placeItems: 'center',
                          fontWeight: 800,
                          fontSize: '1rem',
                          bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.12),
                          color: ACCENT,
                        }}
                      >
                        {problemLetter(index)}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: '0.95rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatProblemTitle(problem.title) || `Problem ${index + 1}`}
                        </Typography>
                        <Chip
                          label={diff}
                          size="small"
                          sx={{
                            mt: 0.5,
                            height: 22,
                            textTransform: 'capitalize',
                            fontWeight: 700,
                            fontSize: '0.68rem',
                            bgcolor: alpha(DIFF_COLOR[diff] || muted, 0.12),
                            color: DIFF_COLOR[diff] || muted,
                            border: 'none',
                          }}
                        />
                      </Box>
                      <Button
                        variant="contained"
                        onClick={() => handleSelectProblem(index)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 800,
                          borderRadius: 2,
                          bgcolor: ACCENT,
                          boxShadow: 'none',
                          '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' },
                        }}
                      >
                        Solve
                      </Button>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ) : (
            <Box sx={{ p: { xs: 1, sm: 1.5 }, height: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                  px: 0.5,
                }}
              >
                <Chip
                  label={problemLetter(selectedProblem)}
                  size="small"
                  sx={{
                    fontWeight: 800,
                    bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.12),
                    color: ACCENT,
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '0.9rem',
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {problems[selectedProblem]?.title}
                </Typography>
                <Button
                  size="small"
                  onClick={() => handleSelectProblem('All')}
                  sx={{ textTransform: 'none', fontWeight: 700, color: muted }}
                >
                  Overview
                </Button>
              </Box>
              <ProblemPage
                key={problems[selectedProblem]?._id || selectedProblem}
                contestId={contestId}
                contestJudgingMode={contest?.judgingMode || "io"}
                problem={problems[selectedProblem]}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Mobile problem drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 'min(300px, 88vw)', bgcolor: surface } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton size="small" onClick={() => setDrawerOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <ProblemRail inDrawer />
      </Drawer>

      {/* Time up */}
      <Dialog
        open={isDialogOpen}
        onClose={handleTimeoutDialogClose}
        disableEscapeKeyDown
        PaperProps={{
          sx: { borderRadius: 3, border: `1px solid ${border}`, maxWidth: 420 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Time’s up</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: muted, lineHeight: 1.6 }}>
            Your contest time has expired. Your session was submitted automatically — you can review
            results now.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={handleTimeoutDialogClose}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: ACCENT,
              '&:hover': { bgcolor: '#0f5c5e' },
            }}
          >
            View results
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm submit */}
      <Dialog
        open={confirmSubmitDialog}
        onClose={() => !submitting && setConfirmSubmitDialog(false)}
        PaperProps={{
          sx: { borderRadius: 3, border: `1px solid ${border}`, maxWidth: 420 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Submit contest?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: muted, lineHeight: 1.6 }}>
            Once you submit, you can’t edit solutions further. Make sure you’ve attempted the
            problems you care about.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setConfirmSubmitDialog(false)}
            disabled={submitting}
            sx={{ textTransform: 'none', fontWeight: 700, color: muted }}
          >
            Keep coding
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: ACCENT,
              '&:hover': { bgcolor: '#0f5c5e' },
            }}
          >
            {submitting ? 'Submitting…' : 'Submit now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success */}
      <Dialog
        open={successDialog}
        onClose={() => navigate(`/contests/${contestId}/result`)}
        PaperProps={{
          sx: { borderRadius: 3, border: `1px solid ${border}`, maxWidth: 420 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleOutlineIcon sx={{ color: '#1a7f37' }} />
          Submitted
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: muted, lineHeight: 1.6 }}>
            Your contest submission is recorded. Head to results to see how you did.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => navigate(`/contests/${contestId}/result`)}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: ACCENT,
              '&:hover': { bgcolor: '#0f5c5e' },
            }}
          >
            View results
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LiveContest;
